// Source detail page — v2 (built from-scratch per the IA in
// docs/SOURCE_PAGE_V2_IA.md). Five sections stacked vertically:
//
//   § Status & Controls   — sticky header with name, state, controls
//   § Inputs              — how data gets in (webhook, upload, form, …)
//   § Processing          — what happens to data (triggers, schedules)
//   § Data                — what the data became (live, BQ, files, errors, replays)
//   § Settings            — configuration that doesn't fit elsewhere
//
// The legacy v1 page (SourceDetailPage.tsx) keeps rendering for
// operators on the older view; a per-page Layout switcher in the
// status section flips between the two. URL flag `?v=2` overrides;
// localStorage 'datahub.sourcePage.layout' = 'v2' | 'legacy' is the
// sticky preference.
//
// Build constraints (operator mandate Apr 30 2026):
//   - Rebuilt from-scratch — no JSX copied from legacy SourceDetailPage.
//   - Anything without flow logic dropped (BuildPhaseCTA absorbed into
//     Processing's empty state; redundant TopHeader env pill not used;
//     "Recent replays" floating strip moved into Data > Replays sub-tab).
//   - Modals (Replay/Upload/SendCustom/HowToSend/etc.) reused as-is —
//     they're independent of the page chrome.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Source } from '@datahub/shared';
import { getSource } from '../api';
import TopHeader from '../components/TopHeader';
import { useToast } from '../components/Toast';
import { StatusControlsV2 } from '../components/v2/StatusControlsV2';
import { InputsRailV2 } from '../components/v2/InputsRailV2';
import { ProcessingPanelV2 } from '../components/v2/ProcessingPanelV2';
import { DataExplorerV2 } from '../components/v2/DataExplorerV2';
import { SettingsCardsV2 } from '../components/v2/SettingsCardsV2';
import { SchemaReadyBanner } from '../components/SchemaReadyBanner';

/** Source detail page (V2 layout). The legacy V1 layout was deleted
 *  2026-05-09 per pre-launch wipe-and-restart contract. */
export default function SourceDetailV2Page(): React.ReactElement | null {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [source, setSource] = useState<Source | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'not_found' | 'error' | 'ready'>('loading');

  useEffect(() => {
    if (!id) return;
    setLoadState('loading');
    getSource(id)
      .then(s => { setSource(s); setLoadState('ready'); })
      .catch(err => {
        if (String(err).includes('404')) setLoadState('not_found');
        else { setLoadState('error'); toast.show(`Load failed: ${(err as Error).message}`, { kind: 'error' }); }
      });
  }, [id, toast]);

  if (!id) return null;
  if (loadState === 'loading') {
    return (
      <>
        <TopHeader breadcrumb={[{ label: 'Sources', to: '/sources' }]} />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>
      </>
    );
  }
  if (loadState === 'not_found') {
    return (
      <>
        <TopHeader breadcrumb={[{ label: 'Sources', to: '/sources' }]} />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-2)' }}>
          Source <code>{id}</code> not found.
        </div>
      </>
    );
  }
  if (loadState === 'error' || !source) {
    return (
      <>
        <TopHeader breadcrumb={[{ label: 'Sources', to: '/sources' }]} />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger)' }}>
          Failed to load source. Refresh to retry.
        </div>
      </>
    );
  }

  return (
    <>
      <TopHeader
        breadcrumb={[{ label: 'Sources', to: '/sources' }]}
      />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 24px 80px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        <SchemaReadyBanner sourceId={source.source_id} />
        <StatusControlsV2 source={source} onSourceChange={setSource} />
        <InputsRailV2 source={source} onSourceChange={setSource} />
        <ProcessingPanelV2 source={source} />
        <DataExplorerV2 source={source} />
        <SettingsCardsV2 source={source} onSourceChange={setSource} />
      </div>
    </>
  );
}
